/**
 * The sun's shadows of a storey's entity tiles (every composite: trees, bushes, boulders, barrels,
 * statues, tents...), for MapRenderingManager to draw and publish with its buildings' shadows: one union,
 * one strength, one softness, and the same shade on the bodies standing in it (building_shade.ts). The
 * shapes are foliage_shadow_geometry.ts's, chosen per tile by tileShadowModel: plants marked
 * `castsShadow` (the procedural grove) cast crown and trunk, other upright art its standing silhouette;
 * grass, flat art and art that bakes its own ground shadow (the older LPC trees and bushes) cast nothing.
 * Every caster also gives its foot, for contact shade.
 */
import { NumberedTileMeta } from "game-common/models"
import { TileMapMetaProvider } from "game-common/tile_map_meta_provider"
import type { ShadowPolygon } from "./building_shadow_geometry"
import { compositeSpritePlacement } from "./composite_tile_placement"
import { contentTextureGeneration } from "./content_textures"
import {
    FoliageShadowOptions, PlantShadowParts, PlantSilhouette, plantShadowParts, plantSilhouette, ShadowStamp, tileShadowModel,
    TileShadowModel, translatePolygon, translateStamp, uprightShadowParts, solidShadowParts,
} from "./foliage_shadow_geometry"
import { compositeArtPixels } from "./sprites/composite_art_pixels"

/**
 * Live tuning, like window.buildingShadow: window.foliageShadow.enabled / .depth (how deep a shadow
 * lies for its width or drawn height) / .silhouettes (false: every plant casts the smooth outline).
 * Strength and softness are window.buildingShadow's: it is all one shadow. Setting
 * window.foliageShadow = false turns these off and leaves the buildings'.
 */
export const FOLIAGE_SHADOW = { enabled: true, depth: 0.5, silhouettes: true, grass: 0.5 }

export const foliageShadowConfig = (): typeof FOLIAGE_SHADOW => {
    if (typeof window === "undefined") return FOLIAGE_SHADOW
    const w = window as any
    if (w.foliageShadow === undefined) w.foliageShadow = FOLIAGE_SHADOW
    const live = w.foliageShadow
    if (live === false) return { ...FOLIAGE_SHADOW, enabled: false }
    return live && typeof live === "object" ? live : FOLIAGE_SHADOW
}

/**
 * A composite as its shadow needs it: where its art is in the atlas, and how big; and `dropRow`, true when
 * the dense-vertical renderer draws it a row below its atlas layout (a parent with children 2+ rows off).
 */
interface Caster { meta: NumberedTileMeta; tilesetId: string; columns: number; width: number; height: number; dropRow: boolean }

/** How far each drawn copy of a dense-vertical tile is lifted (CompositeTileRenderer.denseCopyLifts). */
export type DenseCopyLifts = (tile: string, tileMeta: { denseCopies?: number } | undefined, x: number, y: number) => number[]

// Read once per composite and content generation (a Lab publish can repaint the art).
const silhouettes = new Map<string, PlantSilhouette>()
const models = new Map<string, TileShadowModel>()
const shapes = new Map<string, PlantShadowParts>()
let artGeneration = -1

const plantShape = (caster: Caster, options: FoliageShadowOptions, grassStrength: number): PlantShadowParts | undefined => {
    const generation = contentTextureGeneration()
    if (generation !== artGeneration) {
        silhouettes.clear()
        models.clear()
        shapes.clear()
        artGeneration = generation
    }
    const plant = `${caster.tilesetId}:${caster.meta.tileMeta.compositeTileId}`
    // A newly received room can correct metadata without changing its atlas. Do not retain an old
    // billboard model when the server now sends a solid footprint for the same rock art.
    const meta = caster.meta.tileMeta
    const policy = `${plant}|${meta.shadowModel ?? "auto"}:${!!meta.castsShadow}:${meta.renderZIdxOffset ?? 0}:${meta.tileTypes?.join(",")}`
    const key = `${policy}|${options.sun.x}:${options.sun.y}:${options.depth}:${options.silhouettes !== false}:${grassStrength}`
    let art = silhouettes.get(policy)
    if (!art) {
        const pixels = compositeArtPixels(caster.tilesetId, caster.columns, caster.meta.children || [])
        if (!pixels) return undefined
        art = plantSilhouette(plant, pixels.data, pixels.width, pixels.height)
        silhouettes.set(policy, art)
        models.set(policy, tileShadowModel(meta, pixels.data, pixels.width, pixels.height))
    }
    const model = models.get(policy)
    models.set(plant, model)
    const known = shapes.get(key)
    if (known) return known
    const shape = model === "foliage" ? plantShadowParts(art, options)
        : model === "solid" ? solidShadowParts(art, options)
        : model === "upright" ? uprightShadowParts(art, options)
        : model === "grass" && grassStrength > 0 ? uprightShadowParts(art, options, grassStrength) : { polygons: [], stamps: [] }
    shapes.set(key, shape)
    return shape
}

/**
 * What a storey's entity tiles cast: smooth outlines (spruces), silhouettes (everything else), and their
 * feet; and long grass's lighter silhouettes, `grass`, to draw but not to look shade up in (a meadow would
 * shade itself).
 */
export interface FoliageShadows { polygons: ShadowPolygon[]; stamps: ShadowStamp[]; grass: ShadowStamp[]; feet: ShadowPolygon[]; pending: boolean }

/**
 * The shadows of every composite drawn on this storey that casts (tileShadowModel), in its pixels.
 * `pending` while some art could not be read yet (its sheet still loading): ask again later.
 */
/**
 * Whether a composite casts a shadow of its own (tileShadowModel), once its art has been read; undefined
 * before. A sprite that casts must not look for its shade in its own shadow (sprite_shade.ts).
 */
export const compositeCasts = (tilesetId: string, compositeTileId: string): boolean | undefined => {
    const model = models.get(`${tilesetId}:${compositeTileId}`)
    return model === undefined ? undefined : model === "foliage" || model === "upright" || model === "solid"
}

export const foliageShadows = (provider: TileMapMetaProvider, options: FoliageShadowOptions, denseLifts?: DenseCopyLifts): FoliageShadows => {
    const polygons: ShadowPolygon[] = []
    const stamps: ShadowStamp[] = []
    const grass: ShadowStamp[] = []
    const feet: ShadowPolygon[] = []
    const grassStrength = options.grass ?? FOLIAGE_SHADOW.grass
    let pending = false
    // Tile ids repeat across a forest: resolve each once.
    const casters = new Map<string, Caster | null>()
    const casterFor = (tile: string): Caster | null => {
        let caster = casters.get(tile)
        if (caster !== undefined) return caster
        caster = null
        const meta = provider.resolveTileMeta(tile)
        const tileset = meta?.compositeTileParent && meta.compositeTileId ? provider.resolveTileSet(tile) : undefined
        const composite = tileset && provider.resolveCompositeTileIdTileMeta(meta.compositeTileId, tileset.tilesetId)
        if (composite) {
            const columns = tileset.width
            const cells = (composite.children || []).map(child => child.tileNumber - 1)
            const xs = cells.map(cell => cell % columns), ys = cells.map(cell => Math.floor(cell / columns))
            const parentRow = Math.floor((composite.tileNumber - 1) / columns)
            caster = { meta: composite, tilesetId: tileset.tilesetId, columns,
                width: Math.max(...xs) - Math.min(...xs) + 1, height: Math.max(...ys) - Math.min(...ys) + 1,
                dropRow: Math.max(...ys.map(row => Math.abs(row - parentRow))) > 1 }
        }
        casters.set(tile, caster)
        return caster
    }
    provider.visit(({ tilemap, layerId, sky }) => {
        if (!tilemap || sky) return
        tilemap.forEach((column, x) => column?.forEach((tile, y) => {
            if (!tile || tile === "0") return
            const caster = casterFor(tile)
            if (!caster || !provider.shouldDrawTileAt(x, y, layerId, tilemap)) return
            const shape = plantShape(caster, options, grassStrength)
            if (!shape) { pending = true; return }
            if (!shape.polygons.length && !shape.stamps.length) return
            const at = compositeSpritePlacement(caster.meta, caster, x, y)
            shape.polygons.forEach(polygon => polygons.push(translatePolygon(polygon, at.x, at.y)))
            if (caster.meta.tileMeta.tileTypes?.includes("denseVertical")) {
                // Long grass is drawn as copies, each lifted its own few pixels: a shadow per copy, from its foot.
                const lifts = denseLifts?.(tile, caster.meta.tileMeta, x, y) ?? [0]
                const drop = caster.dropRow ? 32 : 0
                lifts.forEach(lift => shape.stamps.forEach(stamp =>
                    (stamp.strength !== undefined ? grass : stamps).push(translateStamp(stamp, at.x, at.y + drop - lift))))
            } else shape.stamps.forEach(stamp => (stamp.strength !== undefined ? grass : stamps).push(translateStamp(stamp, at.x, at.y)))
            if (shape.foot?.length) feet.push(translatePolygon(shape.foot, at.x, at.y))
        }))
    })
    return { polygons, stamps, grass, feet, pending }
}
